import type { NotificationReportQuery, NotificationReportRun } from 'c-sdk';
import type { NotificationReport } from 'c-entity-types';

// The pure logic behind the scheduled notification report page: delta key columns, the
// report/query request bodies, pause/resume, change detection and run-history display.

export type DraftQuery = {
    id: string;
    source: string;
    query: string;
    filename: string;
    send_new_rows: boolean;
    send_updated_rows: boolean;
    delta_key_columns: string[] | string;
};

export type ExistingQueryDraft = {
    source: string;
    query: string;
    filename: string;
    send_new_rows: boolean;
    send_updated_rows: boolean;
    delta_key_columns: string[] | string;
};

// The editable report fields as the user has typed them (unsaved).
export type ReportDetailsDraft = {
    name: string;
    subject: string;
    body: string;
    interval: string;
    // One email address per line.
    emails: string;
};

// What the query create/update endpoints take. The UI always sends a (possibly empty) key list.
type QueryFields = Pick<
    NotificationReportQuery,
    'query' | 'filename' | 'source' | 'send_new_rows' | 'send_updated_rows'
> & { delta_key_columns: string[] };

// ---- Delta key columns ----
// The columns that identify a row across runs, so a delta delivery can tell new and updated
// rows apart. The API stores a list; the textarea holds one per line (commas also split).

export const normalizeDeltaKeyColumns = (value?: string[] | string | null): string[] => {
    if (Array.isArray(value)) {
        return value.map(entry => String(entry).trim()).filter(entry => entry.length > 0);
    }

    if (typeof value !== 'string') {
        return [];
    }

    return value
        .split(/[,\n]/)
        .map(entry => entry.trim())
        .filter(entry => entry.length > 0);
};

export const deltaKeyColumnsToText = (value?: string[] | string | null): string =>
    normalizeDeltaKeyColumns(value).join('\n');

export const areDeltaKeyColumnsEqual = (
    left?: string[] | string | null,
    right?: string[] | string | null,
): boolean =>
    JSON.stringify(normalizeDeltaKeyColumns(left)) ===
    JSON.stringify(normalizeDeltaKeyColumns(right));

export const detectedColumnsContainKeys = (columns: string[], keys: string[]): boolean =>
    keys.every(key => columns.includes(key));

export const buildColumnOptions = (columns: string[]) =>
    columns.map(column => ({
        label: column,
        value: column,
    }));

export const getRequestErrorMessage = (error: any, fallback: string): string =>
    error?.response?.data?.message ?? error?.message ?? fallback;

// ---- Queries ----

export const getQueryKey = (query: NotificationReportQuery, index: number) =>
    String(query.id ?? query.filename ?? index);

export const normalizeSource = (value?: string | null): NotificationReportQuery['source'] =>
    value === 'mysql' ? 'mysql' : 'athena';

// A create/update response may arrive as the axios response, its `{ data }` body or the query.
export const normalizeQueryResponse = (response: unknown): NotificationReportQuery | null => {
    if (!response) return null;
    if (typeof response === 'object' && response && 'data' in response) {
        const data = (response as { data?: unknown }).data;
        if (data && typeof data === 'object' && 'data' in data) {
            return (data as { data?: NotificationReportQuery }).data ?? null;
        }
        return data as NotificationReportQuery;
    }
    return response as NotificationReportQuery;
};

// The editable copy of a saved query.
export const queryToDraft = (
    query: Partial<NotificationReportQuery> | null | undefined,
): ExistingQueryDraft => ({
    source: query?.source ?? '',
    query: query?.query ?? '',
    filename: query?.filename ?? '',
    send_new_rows: Boolean(query?.send_new_rows),
    send_updated_rows: Boolean(query?.send_updated_rows),
    delta_key_columns: deltaKeyColumnsToText(query?.delta_key_columns),
});

const deliversDelta = (draft: ExistingQueryDraft) => draft.send_new_rows || draft.send_updated_rows;

// A query can be saved once it has a source, SQL and a filename, and — when it sends only new or
// updated rows — the key columns that tell rows apart.
export const isDraftQueryComplete = (query: ExistingQueryDraft): boolean =>
    query.source.trim() !== '' &&
    query.query.trim() !== '' &&
    query.filename.trim() !== '' &&
    (!deliversDelta(query) || normalizeDeltaKeyColumns(query.delta_key_columns).length > 0);

export const canSaveDraftQueries = (draftQueries: DraftQuery[]): boolean =>
    draftQueries.length > 0 && draftQueries.every(isDraftQueryComplete);

// The checks handleSaveExistingQuery makes before sending an update.
export const canSubmitExistingQuery = (draft: ExistingQueryDraft): boolean => {
    if (!draft.query.trim() || !draft.filename.trim() || !draft.source.trim()) return false;
    return !(
        deliversDelta(draft) && normalizeDeltaKeyColumns(draft.delta_key_columns).length === 0
    );
};

// Whether the edits match what is saved (so there is nothing to save).
export const isExistingQueryUnchanged = (
    draft: ExistingQueryDraft,
    query: NotificationReportQuery,
): boolean =>
    (draft.query ?? '') === (query.query ?? '') &&
    (draft.filename ?? '') === (query.filename ?? '') &&
    (draft.source ?? '') === (query.source ?? '') &&
    draft.send_new_rows === Boolean(query.send_new_rows) &&
    draft.send_updated_rows === Boolean(query.send_updated_rows) &&
    areDeltaKeyColumnsEqual(draft.delta_key_columns, query.delta_key_columns);

// Whether a saved query's Save button is disabled (besides while a save is in flight).
export const isExistingQuerySaveDisabled = (
    draft: ExistingQueryDraft,
    query: NotificationReportQuery,
): boolean => {
    if (!draft.query.trim() || !draft.filename.trim() || !draft.source.trim()) return true;
    return isExistingQueryUnchanged(draft, query);
};

// Whether a saved query is the one a just-saved draft became (so the draft can be dropped).
export const draftMatchesSavedQuery = (
    existing: NotificationReportQuery,
    draft: DraftQuery,
): boolean =>
    (existing.query ?? '').trim() === draft.query.trim() &&
    (existing.filename ?? '').trim() === draft.filename.trim() &&
    (existing.source ?? '').trim() === draft.source.trim() &&
    Boolean(existing.send_new_rows) === draft.send_new_rows &&
    Boolean(existing.send_updated_rows) === draft.send_updated_rows &&
    areDeltaKeyColumnsEqual(existing.delta_key_columns, draft.delta_key_columns);

// The body of PUT report-query/:id.
export const buildUpdateQueryPayload = (draft: ExistingQueryDraft): QueryFields => ({
    query: draft.query,
    filename: draft.filename,
    source: draft.source as 'athena' | 'mysql',
    send_new_rows: draft.send_new_rows,
    send_updated_rows: draft.send_updated_rows,
    delta_key_columns: normalizeDeltaKeyColumns(draft.delta_key_columns),
});

// The body of POST report-query (a new query, or one restored by "undo delete").
export const buildCreateQueryPayload = (reportId: number, draft: ExistingQueryDraft) => ({
    report_id: reportId,
    ...buildUpdateQueryPayload(draft),
});

// ---- Report details ----

// One address per line; blank lines dropped. Nothing is validated here.
export const parseRecipients = (text: string): string[] =>
    text
        .split('\n')
        .map(entry => entry.trim())
        .filter(entry => entry.length > 0);

export const recipientsToText = (emails: string[] | null | undefined): string =>
    (emails ?? []).join('\n');

// Whether the typed details match the saved report (whitespace at the ends ignored).
export const isReportDetailsUnchanged = (
    report: Partial<NotificationReport> | null | undefined,
    draft: ReportDetailsDraft,
): boolean =>
    draft.name.trim() === (report?.name ?? '').trim() &&
    draft.subject.trim() === (report?.email_subject ?? '').trim() &&
    draft.body.trim() === (report?.email_body ?? '').trim() &&
    draft.interval.trim() === (report?.interval ?? '').trim() &&
    draft.emails.trim() === recipientsToText(report?.email_addresses).trim();

// The fields PUT report/:id sends. A blank name or interval keeps the saved one; a blank
// subject, body or recipient list clears it.
export const buildReportUpdatePayload = (report: NotificationReport, draft: ReportDetailsDraft) => {
    const nextEmails = parseRecipients(draft.emails);
    const nextName = draft.name.trim();
    const nextSubject = draft.subject.trim();
    const nextBody = draft.body.trim();
    const nextInterval = draft.interval.trim();
    return {
        name: nextName || report.name,
        email_subject: nextSubject.length > 0 ? nextSubject : null,
        email_body: nextBody.length > 0 ? nextBody : null,
        interval: nextInterval.length > 0 ? nextInterval : report.interval,
        email_addresses: nextEmails.length > 0 ? nextEmails : null,
    };
};

// Pausing or resuming: what is sent, and the report shown until the API answers. A paused
// report has no next run.
export const buildPauseToggle = (report: NotificationReport, now: Date) => {
    const nextIsPaused = !report.is_paused;
    return {
        request: { is_paused: nextIsPaused },
        optimistic: {
            ...report,
            is_paused: nextIsPaused,
            paused_at: nextIsPaused ? now.toISOString() : null,
            next_run_at: nextIsPaused ? null : report.next_run_at,
        },
    };
};

// ---- Display ----

// A timestamp from the API (ISO, UTC) in the viewer's locale and timezone; null when missing
// or unreadable.
export const formatReportDate = (value?: string | null): string | null => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleString();
};

export type BytesLabel =
    | { key: 'emptyValue' }
    | { key: 'bytesLabel'; count: number }
    | { key: 'kilobytesLabel' | 'megabytesLabel'; count: string };

// Which translation (under Modules.Admin.NotificationDetail) a file size reads as.
export const bytesLabelOf = (value?: number | null): BytesLabel => {
    if (value === null || value === undefined) return { key: 'emptyValue' };
    if (value < 1024) return { key: 'bytesLabel', count: value };
    if (value < 1024 * 1024) return { key: 'kilobytesLabel', count: (value / 1024).toFixed(1) };
    return { key: 'megabytesLabel', count: (value / (1024 * 1024)).toFixed(1) };
};

// The per-query results of a run.
export const queryRunsOf = (run: NotificationReportRun) => run.query_runs?.data ?? [];
